import { FilterSelect } from '@easydoctor/ui';
import {
  NO_ASSIGNEE,
  QUEUE_SEARCH_MAX_LENGTH,
  REPLY_RECEIVED,
  REPLY_RECEIVED_LABEL,
  TICKET_LIST_SCOPE_STATES,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABELS,
  TICKET_STATE_LABELS,
  UNTRIAGED_LABEL,
  UNTRIAGED_PRIORITY,
  type TeamMember,
  type TicketListScope,
} from '@easyhelp/shared';
import ClearIcon from '@mui/icons-material/Clear';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';
import { QueueSortSelect } from './QueueSortSelect';
import { QUEUE_TOOLBAR_WIDTHS } from './toolbar-widths';
import type { QueueParams } from './queue-params';

/** Attesa dopo l'ultimo tasto prima di cercare: una richiesta per parola, non per lettera. */
export const SEARCH_DEBOUNCE_MS = 300;

/** Valore del select per «nessun filtro». */
const ALL = '';

/** Un filtro della barra: parametro dell'URL, etichetta accessibile, voce «tutti» e opzioni. */
interface QueueFilter {
  key: 'committente' | 'priority' | 'state' | 'assignee' | 'reply';
  label: string;
  allLabel: string;
  options: readonly { value: string; label: string }[];
  /** Etichetta di un valore dell'URL che non è (o non è ancora) fra le opzioni. */
  unknownLabel?: string;
}

interface QueueFilterSelectProps extends Omit<QueueFilter, 'key'> {
  value: string | undefined;
  width: number;
  onChange: (value: string | undefined) => void;
}

/**
 * `FilterSelect` di `@easydoctor/ui` senza etichetta visibile (il nome accessibile è `label`), come i
 * filtri di Pazienti in EasyCare, a larghezza fissa. Il filtro attivo ha il bordo evidenziato.
 */
function QueueFilterSelect({ label, value, allLabel, options, onChange, unknownLabel, width }: QueueFilterSelectProps) {
  // Un valore arrivato dall'URL resta selezionabile anche prima che le opzioni siano caricate.
  const known = !value || options.some((option) => option.value === value);
  const shown = known ? options : [...options, { value: value ?? ALL, label: unknownLabel ?? value ?? '' }];
  return (
    <FilterSelect
      hideLabel
      label={label}
      value={value ?? ALL}
      options={[{ value: ALL, label: allLabel }, ...shown]}
      onChange={(next) => onChange(next || undefined)}
      minWidth={0}
      sx={{
        width: { xs: '100%', sm: width },
        flexShrink: 0,
        ...(value ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' } } : {}),
      }}
    />
  );
}

interface QueueFiltersProps {
  /** Coda o archivio (#123): il filtro «Stato» offre i soli stati dell'ambito. */
  scope?: TicketListScope;
  params: QueueParams;
  /** Committenti presenti nei ticket (dalla risposta della coda). */
  committenti: readonly string[];
  /** Membri del team, per il filtro responsabile. */
  members: readonly Pick<TeamMember, 'id' | 'name'>[];
  onChange: (changes: Partial<QueueParams>) => void;
  /** «Azzera filtri» (conserva l'ordinamento): assente quando non c'è nulla da azzerare, e il pulsante è disattivato. */
  onClear?: () => void;
}

/**
 * Barra della coda e dell'archivio dentro la `MainCard` (PRD-004 US-1, #105, #123), come quella di
 * Pazienti in EasyCare: ricerca, i filtri combinabili senza etichetta (committente, priorità con «Da
 * triare», stato con i tre stati dell'ambito, responsabile con «Non assegnati» e, solo in coda,
 * «Risposta ricevuta» di #122), «Azzera filtri» e «Ordina per» (#47) allineato a destra. Nessun filtro né ordinamento per prodotto (DEC-003). La ricerca parte da sola dopo una breve
 * pausa nella digitazione.
 *
 * **La barra non si ricompone** quando un filtro si attiva o si toglie: ogni campo ha una larghezza
 * fissa ({@link QUEUE_TOOLBAR_WIDTHS}) e «Azzera filtri» occupa sempre il suo posto, disattivato se non
 * c'è nulla da azzerare. «Azzera filtri» e «Ordina per» stanno in un gruppo che non si spezza: quando
 * la riga non basta vanno a capo insieme, il primo a sinistra e il secondo a destra, così «Ordina per»
 * non resta mai da solo. Sotto `sm` i campi sono in colonna, a tutta larghezza.
 *
 * Un filtro nuovo è una voce in più di `filters`, con la sua larghezza.
 */
export function QueueFilters({ scope = 'coda', params, committenti, members, onChange, onClear }: QueueFiltersProps) {
  const [draft, setDraft] = useState(params.q ?? '');
  const [syncedQ, setSyncedQ] = useState(params.q);

  // L'URL cambia anche da fuori (Azzera filtri, indietro): la casella lo segue, senza toccare
  // ciò che si sta scrivendo quando il cambio è la ricerca stessa (spazi finali compresi).
  if (params.q !== syncedQ) {
    setSyncedQ(params.q);
    if (draft.trim() !== (params.q ?? '')) setDraft(params.q ?? '');
  }

  useEffect(() => {
    const q = draft.trim() || undefined;
    if (q === params.q) return undefined;
    const timer = setTimeout(() => onChange({ q }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, params.q, onChange]);

  const filters: readonly QueueFilter[] = [
    {
      key: 'committente',
      label: 'Committente',
      allLabel: 'Tutti i committenti',
      options: committenti.map((committente) => ({ value: committente, label: committente })),
    },
    {
      key: 'priority',
      label: 'Priorità',
      allLabel: 'Ogni priorità',
      options: [
        { value: UNTRIAGED_PRIORITY, label: UNTRIAGED_LABEL },
        ...TICKET_PRIORITIES.map((priority) => ({ value: priority, label: TICKET_PRIORITY_LABELS[priority] })),
      ],
    },
    {
      key: 'state',
      label: 'Stato',
      allLabel: 'Ogni stato',
      options: TICKET_LIST_SCOPE_STATES[scope].map((state) => ({ value: state, label: TICKET_STATE_LABELS[state] })),
    },
    {
      key: 'assignee',
      label: 'Responsabile',
      allLabel: 'Chiunque',
      unknownLabel: 'Membro selezionato',
      options: [
        { value: NO_ASSIGNEE, label: 'Non assegnati' },
        ...members.map((member) => ({ value: member.id, label: member.name })),
      ],
    },
    // Nell'archivio nessun ticket ha «Risposta ricevuta»: il filtro è solo della coda.
    ...(scope === 'coda'
      ? [
          {
            key: 'reply',
            label: 'Risposta',
            allLabel: 'Ogni risposta',
            options: [{ value: REPLY_RECEIVED, label: REPLY_RECEIVED_LABEL }],
          } satisfies QueueFilter,
        ]
      : []),
  ];

  return (
    <Box
      role="search"
      aria-label={scope === 'coda' ? 'Cerca e filtra la coda' : "Cerca e filtra l'archivio"}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        // In colonna niente `wrap`: ogni campo prende la larghezza della barra, non quella del più largo.
        flexWrap: { xs: 'nowrap', sm: 'wrap' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
      }}
    >
      <TextField
        size="small"
        type="search"
        placeholder="Codice, testo, persona"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        sx={{
          flex: { sm: `1 0 ${QUEUE_TOOLBAR_WIDTHS.search.min}px` },
          maxWidth: { sm: QUEUE_TOOLBAR_WIDTHS.search.max },
          // Chrome e Safari aggiungono una loro «×» ai campi `search`: c'è già «Pulisci ricerca».
          '& input::-webkit-search-cancel-button': { WebkitAppearance: 'none', display: 'none' },
        }}
        slotProps={{
          htmlInput: { maxLength: QUEUE_SEARCH_MAX_LENGTH, 'aria-label': 'Cerca' },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: draft ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Pulisci ricerca" onClick={() => setDraft('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
      />
      {filters.map(({ key, ...filter }) => (
        <QueueFilterSelect
          key={key}
          {...filter}
          value={params[key]}
          width={QUEUE_TOOLBAR_WIDTHS[key]}
          onChange={(value) => onChange({ [key]: value })}
        />
      ))}
      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flex: '1 0 auto',
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          startIcon={<FilterAltOffIcon />}
          onClick={onClear}
          disabled={!onClear}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          Azzera filtri
        </Button>
        <QueueSortSelect value={params.sort} onChange={(sort) => onChange({ sort })} />
      </Box>
    </Box>
  );
}
